// Calendar-string helpers for DatePicker. Values are plain IST calendar strings
// (yyyy-MM-dd or yyyy-MM-ddTHH:mm); nothing here converts time zones.
const IST = 'Asia/Kolkata';

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pad = (n: number) => String(n).padStart(2, '0');

function istParts(date: Date): Record<string, string> {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
}

/** Today's IST calendar date as yyyy-MM-dd. */
export function istToday(now = new Date()): string {
  const p = istParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

/** The current IST wall-clock time as HH:mm. */
export function istNowTime(now = new Date()): string {
  const p = istParts(now);
  return `${p.hour}:${p.minute}`;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function toDay(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function splitDay(day: string): [number, number, number] {
  return [Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10))];
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(day: string): number {
  const [y, m, d] = splitDay(day);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = splitDay(day);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Moves by whole months, keeping the day where it exists (31 Jan + 1 → 28/29 Feb). */
export function addMonths(day: string, n: number): string {
  const [y, m, d] = splitDay(day);
  const first = new Date(Date.UTC(y, m - 1 + n, 1));
  const year = first.getUTCFullYear();
  const month = first.getUTCMonth() + 1;
  return toDay(year, month, Math.min(d, daysInMonth(year, month)));
}

export type Meridiem = 'AM' | 'PM';

/** 24-hour "HH" → 12-hour clock: "00" → 12 AM, "12" → 12 PM, "14" → 2 PM. */
export function to12Hour(hh: string): { hour: string; meridiem: Meridiem } {
  const h = Number(hh);
  return { hour: String(h % 12 || 12), meridiem: h < 12 ? 'AM' : 'PM' };
}

/** 12-hour clock → 24-hour "HH": 12 AM → "00", 12 PM → "12", 2 PM → "14". */
export function to24Hour(hour: string, meridiem: Meridiem): string {
  return pad((Number(hour) % 12) + (meridiem === 'PM' ? 12 : 0));
}

/** "17 Oct 2026" / "17 Oct 2026, 2:30 PM"; '' for an empty or malformed value. */
export function formatDisplay(value: string, withTime: boolean): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(value);
  if (!match) return '';
  const [, y, m, d, hh, mm] = match;
  const date = `${d} ${MONTHS[Number(m) - 1]} ${y}`;
  if (!withTime || !hh) return date;
  const { hour, meridiem } = to12Hour(hh);
  return `${date}, ${hour}:${mm} ${meridiem}`;
}

/** "17 October 2026" for screen readers. */
export function formatLong(day: string): string {
  const [y, m, d] = splitDay(day);
  return `${d} ${MONTH_NAMES[m - 1]} ${y}`;
}

const DATE_PATTERNS: Array<[RegExp, (m: RegExpExecArray) => [string, string, string]]> = [
  [/^(\d{4})-(\d{1,2})-(\d{1,2})/, (m) => [m[1], m[2], m[3]]],
  [/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/, (m) => [m[3], m[2], m[1]]],
  [/^(\d{1,2})\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})/, (m) => [m[3], m[2], m[1]]],
];

function monthNumber(token: string): number {
  if (/^\d+$/.test(token)) return Number(token);
  const index = MONTHS.findIndex((name) => token.toLowerCase().startsWith(name.toLowerCase()));
  return index + 1;
}

/**
 * Reads what a person typed ("17 Oct 2026", "17/10/2026", "17-10-2026", "2026-10-17", plus
 * " 2:30 PM", " 2:30pm", " 14:30" or "T14:30" when withTime) and returns the canonical
 * 24-hour string, or null if it is not a complete, real calendar value.
 */
export function parseInput(text: string, withTime: boolean): string | null {
  const trimmed = text.trim();
  for (const [pattern, pick] of DATE_PATTERNS) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    const [ys, ms, ds] = pick(match);
    const year = Number(ys);
    const month = monthNumber(ms);
    const day = Number(ds);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
    const date = toDay(year, month, day);
    const rest = trimmed.slice(match[0].length).trim();
    if (!withTime) return rest === '' ? date : null;
    const time = /^(?:,\s*|T\s*)?(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i.exec(rest);
    if (!time) return null;
    const hour = Number(time[1]);
    const minute = Number(time[2]);
    if (minute > 59) return null;
    if (time[3]) {
      if (hour < 1 || hour > 12) return null;
      return `${date}T${to24Hour(String(hour), time[3].toUpperCase() as Meridiem)}:${pad(minute)}`;
    }
    if (hour > 23) return null;
    return `${date}T${pad(hour)}:${pad(minute)}`;
  }
  return null;
}
